/**
 * Errores de la web en lenguaje claro.
 *
 * El mapeo de verdad (qué texto, si se puede reintentar, qué campo) vive en
 * `@fitness/core` y lo comparte la app móvil; aquí sólo se traduce lo que
 * lanza el cliente de la API a su entrada. Sin DOM ni React, para poder
 * probarlo con `node --test`.
 */
import { isApiClientError, isNetworkError } from '@fitness/api-client';
import {
  friendlyError,
  nextLoginFailureCount,
  type ErrorSource,
  type FriendlyError,
  type FriendlyErrorOptions,
} from '@fitness/core';

export type { FriendlyError, FriendlyErrorOptions };

/** Lo que se sabe de un error cualquiera, en el formato del mapeo común. */
export function toErrorSource(error: unknown): ErrorSource {
  if (isNetworkError(error)) return { type: 'network' };
  if (isApiClientError(error)) {
    return {
      type: 'api',
      status: error.status,
      code: error.code,
      details: error.details,
      retryAfterSeconds: error.retryAfterSeconds,
    };
  }
  return { type: 'unknown' };
}

/** El error explicado: texto, si tiene sentido reintentar y errores por campo. */
export function explainError(error: unknown, options?: FriendlyErrorOptions): FriendlyError {
  return friendlyError(toErrorSource(error), options);
}

/** Sólo el texto para la persona; lo que pintan los `ErrorBanner`. */
export function describeError(error: unknown, options?: FriendlyErrorOptions): string {
  return explainError(error, options).message;
}

/** Fallos de contraseña seguidos tras este error (ver `nextLoginFailureCount`). */
export function countLoginFailure(previous: number, error: unknown): number {
  return nextLoginFailureCount(previous, toErrorSource(error));
}

/**
 * Lleva los errores por campo de la API a un formulario de react-hook-form.
 * Sólo los campos que el formulario tiene; devuelve si ha marcado alguno, para
 * no repetir arriba un aviso genérico cuando el problema ya se ve en su campo.
 */
export function applyFieldErrors<Field extends string>(
  explained: FriendlyError,
  fields: readonly Field[],
  setError: (field: Field, error: { message: string }, options?: { shouldFocus: boolean }) => void,
): boolean {
  let first = true;
  for (const field of fields) {
    const message = explained.fieldErrors[field];
    if (message === undefined) continue;
    setError(field, { message }, { shouldFocus: first });
    first = false;
  }
  return !first;
}
